import { useEffect, useState } from "react";
import type { ComponentConfig } from '@puckeditor/core';
import { FormBlock } from './form-block';
import { FormBlockFields } from "./form-block.fields";
import type { FormBlockProps } from './form-block';
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { getWindowJobsiteConfig } from '../../types/runtime-config';

/**
 * The Form-block picker: the business's placeable forms (contact + user
 * forms), fetched from the public list. Runs in the dashboard's fields panel,
 * which exposes the editing site via window.__SERVICEPRO_CONFIG__ (the same
 * source page-settings-fields uses).
 */
const FormKeyField = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: string) => void;
}) => {
  const [options, setOptions] = useState<{ key: string; name: string }[] | null>(null);

  useEffect(() => {
    const config = getWindowJobsiteConfig(),
      apiUrl = config?.apiUrl ?? "";
    if (!config?.siteId) {
      setOptions([]);
      return;
    }
    let alive = true;
    fetch(`${apiUrl}/api/sites/${config.siteId}/forms`)
      .then((res) => {
        if (res.ok) {
          return res.json() as Promise<{ forms: { key: string; name: string }[] }>;
        }
        return null;
      })
      .then((body) => {
        if (alive) {
          setOptions(body?.forms ?? []);
        }
      })
      .catch(() => {
        if (alive) {
          setOptions([]);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <select
      value={value ?? ""}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px", width: "100%" }}
    >
      <option value="">{options === null ? "Loading forms…" : "Choose a form…"}</option>
      {(options ?? []).map((o) => (
        <option key={o.key} value={o.key}>
          {o.name}
        </option>
      ))}
    </select>
  );
};

// The builder-independent props Puck manages; resolved business context is
// Injected by render() below.
type FormBlockPuckProps = Omit<FormBlockProps, "businessSlug" | "hasTrackingPhone" | "editorHint">;

const FormBlockConfig: ComponentConfig<FormBlockPuckProps> = {
  defaultProps: {
    backgroundColor: "",
    formKey: "contact",
    heading: "",
    subheading: "",
    submitButtonText: "",
    successMessage: "Thank you! We'll be in touch soon.",
  },
  fields: {
    formKey: {
      ...FormBlockFields.formKey,
      render: ({ value, onChange }) => (
        <FormKeyField value={value} onChange={onChange} />
      ),
      type: "custom",
    },
    heading: { ...FormBlockFields.heading, contentEditable: true },
    subheading: { ...FormBlockFields.subheading, contentEditable: true },
    submitButtonText: FormBlockFields.submitButtonText,
    successMessage: FormBlockFields.successMessage,
    anchorId: FormBlockFields.anchorId,
    backgroundColor: FormBlockFields.backgroundColor,
  },
  label: "Form",
  render: ({ id, puck, ...props }) => {
    const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business;

    return (
      <FormBlock
        id={id}
        {...props}
        businessSlug={business?.slug}
        hasTrackingPhone={Boolean((business)?.hasTrackingPhone)}
        editorHint={Boolean(windowConfig?.isEditing)}
      />
    );
  },
};

export { FormBlockConfig };
